import { useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import { Code2, Server, Database, Layers, ChevronLeft, ChevronRight } from 'lucide-react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import ReactLogo from "@/assets/images/websites/React_logo.png";
import PhpLogo from "@/assets/images/websites/PHP-logo.png";
import LaravelLogo from "@/assets/images/websites/Laravel-logo.png";
import WordpressLogo from "@/assets/images/websites/wordpress-logo.png";
import AngularLogo from "@/assets/images/websites/angular-logo.png";
import NodeJSLogo from "@/assets/images/websites/node.png";
import SymfonyLogo from "@/assets/images/websites/symfony.png";
import MagentoLogo from "@/assets/images/websites/magento.png";
import MySQLLogo from "@/assets/images/websites/MySQL.png";
import PostGreLogo from "@/assets/images/websites/postgresql.png";

export default function StackUsed() {
    const [activeCategory, setActiveCategory] = useState<number>(0);
    const swiperRef = useRef<SwiperType | null>(null);

    const categories = [
        {
            id: 'frontend',
            title: 'Frontend',
            icon: Code2,
            gradient: 'from-slate-600 to-slate-700',
            bgGradient: 'from-slate-800/40 to-slate-900/40',
            cardBg: 'from-blue-900/10 to-cyan-900/10',
            cardBorder: 'border-blue-500/20',
            cardHoverBorder: 'hover:border-blue-500/40',
            border: 'border-slate-600/40',
            shadowColor: 'shadow-slate-700/30',
            glowColor: 'slate-500/20',
            accentColor: 'text-slate-400',
            ambientBg: 'bg-gradient-to-br from-blue-500/8 via-cyan-500/5 to-transparent',
            description: 'Building beautiful, responsive user interfaces',
            technologies: [
                { image: ReactLogo, alt: 'React', name: 'React', description: 'A JavaScript library for building UIs', width: '120px', height: '120px' },
                { image: AngularLogo, alt: 'Angular', name: 'Angular', description: 'Platform for building web applications', width: '120px', height: '120px' }
            ]
        },
        {
            id: 'backend',
            title: 'Backend',
            icon: Server,
            gradient: 'from-zinc-600 to-zinc-700',
            bgGradient: 'from-zinc-800/40 to-zinc-900/40',
            cardBg: 'from-purple-900/10 to-violet-900/10',
            cardBorder: 'border-purple-500/20',
            cardHoverBorder: 'hover:border-purple-500/40',
            border: 'border-zinc-600/40',
            shadowColor: 'shadow-zinc-700/30',
            glowColor: 'zinc-500/20',
            accentColor: 'text-zinc-400',
            ambientBg: 'bg-gradient-to-br from-purple-500/8 via-violet-500/5 to-transparent',
            description: 'Powering robust server-side solutions',
            technologies: [
                { image: PhpLogo, alt: 'PHP', name: 'PHP', description: 'Server-side scripting language', width: '98px', height: '98px' },
                { image: LaravelLogo, alt: 'Laravel', name: 'Laravel', description: 'Elegant PHP framework', width: '98px', height: '98px' },
                { image: SymfonyLogo, alt: 'Symfony', name: 'Symfony', description: 'PHP framework for web applications', width: '98px', height: '98px' },
                { image: NodeJSLogo, alt: 'Node.js', name: 'Node.js', description: 'JavaScript runtime for scalable applications', width: '98px', height: '98px' }
            ]
        },
        {
            id: 'database',
            title: 'Database',
            icon: Database,
            gradient: 'from-neutral-600 to-neutral-700',
            bgGradient: 'from-neutral-800/40 to-neutral-900/40',
            cardBg: 'from-emerald-900/10 to-green-900/10',
            cardBorder: 'border-emerald-500/20',
            cardHoverBorder: 'hover:border-emerald-500/40',
            border: 'border-neutral-600/40',
            shadowColor: 'shadow-neutral-700/30',
            glowColor: 'neutral-500/20',
            accentColor: 'text-neutral-400',
            ambientBg: 'bg-gradient-to-br from-emerald-500/8 via-green-500/5 to-transparent',
            description: 'Reliable data storage and management',
            technologies: [
                { image: PostGreLogo, alt: 'PostgreSQL', name: 'PostgreSQL', description: 'Advanced open source database', width: '130px', height: '130px' },
                { image: MySQLLogo, alt: 'MySQL', name: 'MySQL', description: 'Popular relational database', width: '130px', height: '130px' },
            ]
        },
        {
            id: 'cms',
            title: 'CMS & E-commerce',
            icon: Layers,
            gradient: 'from-gray-600 to-gray-700',
            bgGradient: 'from-gray-800/40 to-gray-900/40',
            cardBg: 'from-amber-900/10 to-orange-900/10',
            cardBorder: 'border-amber-500/20',
            cardHoverBorder: 'hover:border-amber-500/40',
            border: 'border-gray-600/40',
            shadowColor: 'shadow-gray-700/30',
            glowColor: 'gray-500/20',
            accentColor: 'text-gray-300',
            ambientBg: 'bg-gradient-to-br from-amber-500/8 via-orange-500/5 to-transparent',
            description: 'Content management and e-commerce platforms',
            technologies: [
                { image: WordpressLogo, alt: 'WordPress', name: 'WordPress', description: 'World\'s most popular CMS platform', width: '130px', height: '130px' },
                { image: MagentoLogo, alt: 'Magento', name: 'Magento', description: 'Powerful e-commerce platform', width: '130px', height: '130px' }
            ]
        }
    ];

    const handleTabClick = (index: number) => {
        setActiveCategory(index);
        if (swiperRef.current) {
            swiperRef.current.slideTo(index);
        }
    };

    return (
        <section className="relative py-20 lg:pb-28 lg:pt-12 overflow-hidden">
            <div className="container relative z-10">
                {/* Header */}
                <div className="text-center mb-16">
                    <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/5 rounded-full border border-white/10 backdrop-blur-sm text-xs md:text-sm font-semibold text-gray-300 tracking-wider uppercase">
                        Technologies
                    </span>
                    <h2 className="font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
                        Our Technology Stack
                    </h2>
                    <p className="text-gray-300 md:text-lg max-w-2xl mx-auto">
                        Enterprise-grade tools and frameworks powering exceptional solutions
                    </p>
                </div>

                {/* Main Category Slider */}
                <div className="relative max-w-7xl mx-auto mb-12">
                    {/* Custom Navigation Buttons - positioned outside overflow-hidden wrapper */}
                    <button className="custom-prev hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-slate-800/60 backdrop-blur-sm border border-slate-600/40 items-center justify-center text-slate-300 hover:bg-slate-700/80 hover:border-slate-500/60 transition-all duration-300 hover:scale-110 group shadow-lg shadow-slate-900/50">
                        <ChevronLeft className="w-6 h-6 group-hover:text-white transition-colors" />
                    </button>
                    <button className="custom-next hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-slate-800/60 backdrop-blur-sm border border-slate-600/40 items-center justify-center text-slate-300 hover:bg-slate-700/80 hover:border-slate-500/60 transition-all duration-300 hover:scale-110 group shadow-lg shadow-slate-900/50">
                        <ChevronRight className="w-6 h-6 group-hover:text-white transition-colors" />
                    </button>

                    {/* ✅ FIX: overflow-hidden wrapper only around the Swiper, with mx-14 to give space for nav buttons */}
                    <div className="overflow-hidden mx-0 md:mx-14">
                        <Swiper
                            modules={[Autoplay, Pagination, Navigation]}
                            spaceBetween={20}
                            slidesPerView={1}
                            centeredSlides={true}
                            autoplay={{
                                delay: 5000,
                                disableOnInteraction: true,
                                pauseOnMouseEnter: true
                            }}
                            navigation={{
                                prevEl: '.custom-prev',
                                nextEl: '.custom-next',
                            }}
                            onSwiper={(swiper) => {
                                swiperRef.current = swiper;
                            }}
                            onSlideChange={(swiper) => {
                                setActiveCategory(swiper.activeIndex);
                            }}
                            className="category-swiper"
                        >
                            {categories.map((category) => {
                                return (
                                    <SwiperSlide key={category.id}>
                                        <div className={`absolute inset-0 ${category.ambientBg} pointer-events-none opacity-40`} />

                                        <div className="px-4 sm:px-8 py-2 relative z-10">
                                            {/* Technologies Grid */}
                                            <div className={`grid gap-4 sm:gap-5 ${
                                                category.technologies.length === 2 ? 'grid-cols-1 xs:grid-cols-2 max-w-xl mx-auto' :
                                                category.technologies.length === 3 ? 'grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 max-w-2xl mx-auto' :
                                                category.technologies.length === 4 ? 'grid-cols-2 sm:grid-cols-4 max-w-5xl mx-auto' :
                                                'grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 max-w-5xl mx-auto'
                                            }`}>
                                                {category.technologies.map((tech, techIndex) => {
                                                    return (
                                                        <div
                                                            key={techIndex}
                                                            className="tech-card h-44 sm:h-48 group"
                                                            style={{ animationDelay: `${techIndex * 0.08}s` }}
                                                        >
                                                            <div className={`h-full rounded-2xl bg-gradient-to-br ${category.cardBg} border ${category.cardBorder} ${category.cardHoverBorder} flex flex-col items-center justify-center p-4 sm:p-6 transition-all duration-500 backdrop-blur-sm group-hover:shadow-2xl ${category.shadowColor} group-hover:scale-105`}>
                                                                <div className="relative">
                                                                    <div className={`absolute inset-0 bg-${category.glowColor} rounded-full blur-2xl opacity-0 group-hover:opacity-50 transition-opacity duration-500`} />
                                                                    <img
                                                                        src={tech.image}
                                                                        alt={tech.alt}
                                                                        style={{
                                                                            width: tech.width || '96px',
                                                                            height: tech.height || '96px'
                                                                        }}
                                                                        className="relative object-contain drop-shadow-2xl transform group-hover:scale-110 transition-all duration-500 filter group-hover:brightness-125"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                );
                            })}
                        </Swiper>
                    </div>
                </div>

                {/* Category Tab Buttons */}
                <div className="flex justify-center gap-2 sm:gap-3 flex-wrap px-4">
                    {categories.map((category, index) => {
                        const IconComponent = category.icon;
                        const isActive = activeCategory === index;
                        return (
                            <button
                                key={category.id}
                                onClick={() => handleTabClick(index)}
                                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border transition-all duration-300 ${
                                    isActive
                                        ? `bg-gradient-to-r ${category.gradient} border-transparent text-white shadow-lg ${category.shadowColor} scale-105`
                                        : `bg-slate-900/40 ${category.border} text-gray-300 hover:text-white hover:bg-slate-800/60 backdrop-blur-sm hover:scale-105`
                                }`}
                            >
                                <IconComponent className="w-4 h-4" />
                                <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">
                                    {category.title}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <style>{`
                /* Tech Card Animation */
                .tech-card {
                    animation: fadeInUp 0.6s ease-out both;
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Swiper Customization */
                /* ✅ FIX: Changed overflow from visible to hidden */
                .category-swiper {
                    overflow: hidden;
                    padding: 0;
                }

                .category-swiper .swiper-slide {
                    opacity: 1;
                }

                /* Extra small breakpoint */
                @media (min-width: 475px) {
                    .xs\\:grid-cols-2 {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }
                }
            `}</style>
        </section>
    );
}